import { MoreHorizontal } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { Pressable } from '@unistocks/components/basic/pressable';
import { ConfirmDelete } from '@unistocks/components/delete-button/delete-button';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { Heading } from '@unistocks/components/text/text';
import {
  AddOrUpdateSectionForm,
  SectionFormData
} from '@unistocks/features/portfolios/components/forms/add-or-update-section-form';
import {
  getGenericColumn,
  validateSectionProperty
} from '@unistocks/features/portfolios/components/portfolio-table/utils';
import { useTableColumns } from '@unistocks/features/portfolios/hooks/use-table-columns';
import {
  invalidatePortfolioQueriesById,
  isPortfolio as isPortfolioCheck
} from '@unistocks/features/portfolios/portfolio-utils';
import { t } from '@unistocks/localization';
import { PortfolioDto, SortOrder } from '@unistocks/proxy/axios-client';
import {
  portfoliosGetQueryKey,
  setPortfoliosGetData,
  usePortfoliosDeleteSectionMutation,
  usePortfoliosSortSectionMutation,
  usePortfoliosUpdateSectionMutation
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  setWatchlistsGetData,
  useWatchlistsDeleteSectionMutation,
  useWatchlistsSortSectionMutation,
  useWatchlistsUpdateSectionMutation,
  watchlistsGetQueryKey
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { UNISTOCKS_ARROW } from '@unistocks/shared/consts';
import { useToggle } from '@unistocks/shared/hooks/use-toggle';
import { isDefined } from '@unistocks/shared/utils/util';
import React, { useCallback } from 'react';
import { isWeb, useMedia, ViewProps } from 'tamagui';
import { TableHeaderCell } from './table-cell';
import { PortfolioTableColumn, TableSectionProps } from './types';

type CaptionProps = TableSectionProps;

const Caption: React.FC<CaptionProps> = ({ section, portfolio }) => {
  const queryClient = useQueryClient();
  const isPortfolio = isPortfolioCheck(portfolio);
  const [menuOpen, toggleMenuOpen, onOpenChange] = useToggle(false);

  const { mutate: deletePortfolioSection, isPending: isDeletingPortfolioSection } =
    usePortfoliosDeleteSectionMutation(portfolio?.id, section.id, {
      onSuccess: res => {
        toggleMenuOpen();
        setPortfoliosGetData(queryClient, () => res, portfolio.id);
      }
    });

  const { mutate: deleteWatchlistSection, isPending: isDeletingWatchlistSection } =
    useWatchlistsDeleteSectionMutation(portfolio?.id, section.id, {
      onSuccess: res => {
        toggleMenuOpen();
        setWatchlistsGetData(queryClient, () => res, portfolio.id);
      }
    });

  const { mutateAsync: updatePortfolioSection } = usePortfoliosUpdateSectionMutation(
    portfolio?.id,
    section.id,
    {
      onSettled: () => {
        invalidatePortfolioQueriesById(queryClient, portfolio.id);
      }
    }
  );

  const { mutateAsync: updateWatchlistSection } = useWatchlistsUpdateSectionMutation(
    portfolio?.id,
    section.id,
    {
      onSettled: () => {
        invalidatePortfolioQueriesById(queryClient, portfolio.id);
      }
    }
  );

  const { mutateAsync: sortPortfolioSection } = usePortfoliosSortSectionMutation(
    portfolio?.id,
    section.id,
    {
      onMutate: async () => {
        const queryKey = portfoliosGetQueryKey(portfolio.id);
        await queryClient.cancelQueries({ queryKey });
      },
      onSuccess: res => {
        setPortfoliosGetData(queryClient, () => res, portfolio.id);
      },
      onSettled: () => {
        invalidatePortfolioQueriesById(queryClient, portfolio.id);
      }
    }
  );

  const { mutateAsync: sortWatchlistSection } = useWatchlistsSortSectionMutation(
    portfolio?.id,
    section.id,
    {
      onMutate: async () => {
        const queryKey = portfoliosGetQueryKey(portfolio.id);
        await queryClient.cancelQueries({ queryKey });
      },
      onSuccess: res => {
        setWatchlistsGetData(queryClient, () => res, portfolio.id);
      },
      onSettled: () => {
        invalidatePortfolioQueriesById(queryClient, portfolio.id);
      }
    }
  );

  const onSubmit = useCallback(
    async (values: SectionFormData) => {
      const sortFn = isPortfolio ? sortPortfolioSection : sortWatchlistSection;
      const updateFn = isPortfolio ? updatePortfolioSection : updateWatchlistSection;

      const isSortingChanged =
        isDefined(values.sorting.property) &&
        isDefined(values.sorting.sortOrder) &&
        (values.sorting.property !== section.sorting.property ||
          values.sorting.sortOrder !== section.sorting.sortOrder);

      // Update must be completed before sorting to not overwrite the sorting
      await updateFn(
        { name: values.name },
        {
          onSuccess: () => {
            if (!isSortingChanged) {
              toggleMenuOpen();
            }
          }
        }
      );

      if (isSortingChanged) {
        await sortFn(
          {
            sorting: {
              ...section.sorting,
              property: values.sorting.property!,
              sortOrder: values.sorting.sortOrder!
            },
            sectionId: section.id,
            assets: null
          },
          {
            onSuccess: toggleMenuOpen
          }
        );
      }
    },
    [
      isPortfolio,
      sortPortfolioSection,
      sortWatchlistSection,
      updatePortfolioSection,
      updateWatchlistSection,
      section.sorting,
      section.id,
      toggleMenuOpen
    ]
  );

  return (
    <Row mx={'$2'} py={'$2'} alignItems={'center'}>
      <Image size={'$2'} source={UNISTOCKS_ARROW} contentFit={'contain'} />

      <Heading flex={1} fontWeight={'$6'}>
        {section.name}
      </Heading>

      {(portfolio as PortfolioDto).isReadonly ? null : (
        <Pressable withFeedback withScale hitSlop={12} onPress={toggleMenuOpen}>
          <MoreHorizontal />
        </Pressable>
      )}

      <Sheet
        open={menuOpen}
        onOpenChange={onOpenChange}
        headerIcon={<Image size={'$2.5'} source={UNISTOCKS_ARROW} />}
        headerTitle={section.name}
        headerRight={
          <ConfirmDelete
            message={t(
              isPortfolio
                ? '::Portfolio:Category:Delete:Message'
                : '::Watchlist:Category:Delete:Message',
              { 0: section.name }
            )}
            onConfirm={isPortfolio ? deletePortfolioSection : deleteWatchlistSection}
            disabled={isDeletingPortfolioSection || isDeletingWatchlistSection}
          />
        }
        index={1}
      >
        <AddOrUpdateSectionForm portfolio={portfolio} initialValues={section} onSubmit={onSubmit} />
      </Sheet>
    </Row>
  );
};

Caption.displayName = 'Caption';

export type TableSectionHeaderProps = TableSectionProps & ViewProps;

export const TableSectionHeader: React.FC<TableSectionHeaderProps> = ({
  portfolio,
  section,
  ...props
}) => {
  const { isReadonly } = portfolio;
  const { id: sectionId, assets, sorting } = section;
  const queryClient = useQueryClient();
  const media = useMedia();
  const { selected: selectedColumns } = useTableColumns(portfolio);

  const { mutate: sortPortfolioSection, isPending: isLoadingPortfolioSort } =
    usePortfoliosSortSectionMutation(portfolio?.id, sectionId, {
      onMutate: async () => {
        const queryKey = portfoliosGetQueryKey(portfolio.id);
        await queryClient.cancelQueries({ queryKey });
      },
      onSuccess: res => {
        setPortfoliosGetData(queryClient, () => res, portfolio.id);
      },
      onSettled: () => {
        invalidatePortfolioQueriesById(queryClient, portfolio.id);
      }
    });

  const { mutate: sortWatchlistSection, isPending: isLoadingWatchlistSort } =
    useWatchlistsSortSectionMutation(portfolio?.id, sectionId, {
      onMutate: async () => {
        const queryKey = watchlistsGetQueryKey(portfolio?.id);
        await queryClient.cancelQueries({ queryKey });
      },
      onSuccess: res => {
        setWatchlistsGetData(queryClient, () => res, portfolio.id);
      },
      onSettled: () => {
        invalidatePortfolioQueriesById(queryClient, portfolio.id);
      }
    });

  const sort = useCallback(
    (prop: string) => {
      const sortFn = isPortfolioCheck(portfolio) ? sortPortfolioSection : sortWatchlistSection;

      sortFn({
        sorting: {
          ...section.sorting,
          property: prop,
          sortOrder:
            section.sorting.property === prop
              ? section.sorting.sortOrder === SortOrder.Ascending
                ? SortOrder.Descending
                : SortOrder.Ascending
              : SortOrder.Ascending
        },
        sectionId,
        assets: null
      });
    },
    [portfolio, section.sorting, sectionId, sortPortfolioSection, sortWatchlistSection]
  );

  const isLoading = isLoadingPortfolioSort || isLoadingWatchlistSort;

  return (
    <Column {...props}>
      <Caption section={section} portfolio={portfolio} />

      <Row
        bg={'$color4'}
        px={'$2'}
        h={'$2'}
        gap={0}
        alignItems={'center'}
        btlr={'$2'}
        btrr={'$2'}
        $md={{
          px: '$3'
        }}
      >
        <TableHeaderCell
          column={{ size: undefined } as any}
          w={'$2.5'}
          display="none"
          $md={{ display: 'block', mr: '$3' }}
        />
        <TableHeaderCell
          column={nameCol}
          sorting={sorting}
          textAlign={'left'}
          isLoading={isLoading}
          onPress={isWeb ? sort : undefined}
        >
          {t('::Name')}
        </TableHeaderCell>
        {selectedColumns.map(column => (
          <TableHeaderCell
            key={column.prop}
            column={column}
            sorting={sorting}
            justifyContent="flex-end"
            isLoading={isLoading}
            onPress={isWeb ? sort : undefined}
          >
            {validateSectionProperty(assets, column.prop)
              ? column.displayName.toLocaleUpperCase()
              : ''}
          </TableHeaderCell>
        ))}
        {media.lg && !isReadonly ? (
          <TableHeaderCell column={settingsCol} w={'$1.5'} ml={'$3'} />
        ) : null}
      </Row>
    </Column>
  );
};

const nameCol = getGenericColumn('name');
const settingsCol: PortfolioTableColumn = {
  prop: 'settings',
  displayName: ''
};

TableSectionHeader.displayName = 'TableSectionHeader';
